import { EnvironmentService } from '../environment/environment-service';
import { getAndCreatePanalytHeaders } from '../utilFunctions/requestHeaders';
import { HasDependencies, hashString } from '../v2/api/types';
import { ErrorTracker } from '../v2/common/components/errors/trackers/ErrorTracker';
import { ErrorGroups } from '../v2/common/components/errors/types';
import { Domains } from '../v2/constants';

export class RequestService implements HasDependencies {
  constructor(
    readonly errorTracker: ErrorTracker,
    readonly environmentService: EnvironmentService
  ) {}

  public getDeps = () => [
    hashString([
      ...this.errorTracker.getDeps(),
      ...this.environmentService.getDeps(),
    ]),
  ];

  public staleRequestsMap = new Map<RequestInit, AbortController>();

  private async client(
    url: string,
    domain: Domains | null,
    token: string,
    options?: RequestInit
  ): Promise<any> {
    const requestOptions = this.decorateOptions(token, options);
    const requestData = {
      requestData: {
        domain,
        url,
        requestOptions,
      },
    };
    const controller = new AbortController();
    this.staleRequestsMap.set(requestOptions, controller);
    const response = await fetch(url, {
      ...requestOptions,
      signal: controller.signal,
    });
    let responseJson: JSON | null = null;
    if (response.status !== 204 && response.status !== 202) {
      responseJson = await response.json();
    }
    if (!response.ok && response.status !== 404) {
      const error = new Error(`API ERROR at ${url}`);
      const contexts = {
        ...requestData,
        responseBody: responseJson,
      };
      this.errorTracker.trackError({
        error,
        contexts,
        tags: {
          'panalyt.errorGroup': ErrorGroups.API,
          statusCode: response.status,
        },
      });
      this.staleRequestsMap.delete(requestOptions);
    }
    this.staleRequestsMap.delete(requestOptions);
    if (responseJson === null) {
      return ''; // for backward compatibility
    } else {
      return { status: response.status, ...responseJson };
    }
  }

  public async get(
    url: string,
    domain: Domains,
    token: string,
    options: RequestInit = {}
  ): Promise<any> {
    return this.client(url, domain, token, options);
  }

  // TODO: we need a better request service that actually parse responses and returns proper types
  // For now I just want to make it clear that domain and token are not set instead of making them optional for all requests
  public async postUnauth<T>(
    url: string,
    data: T,
    options: RequestInit = {}
  ): Promise<any> {
    const requestOptions = {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    };
    return this.client(url, null, '', requestOptions);
  }

  public async post<T>(
    url: string,
    domain: Domains,
    token: string,
    data: T,
    options: RequestInit = {}
  ): Promise<any> {
    const requestOptions = {
      ...options,
      method: 'POST',
      body: JSON.stringify(data),
    };
    return this.client(url, domain, token, requestOptions);
  }

  public async postFile<T>(
    url: string,
    domain: Domains,
    token: string,
    data: any,
    options: RequestInit = {}
  ): Promise<any> {
    const requestOptions: RequestInit = {
      ...options,
      method: 'POST',
      body: data,
    };
    return this.client(url, domain, token, requestOptions);
  }

  public async put<T>(
    url: string,
    domain: Domains,
    token: string,
    data: T,
    options?: RequestInit
  ): Promise<any> {
    const requestOptions = {
      ...options,
      method: 'PUT',
      body: JSON.stringify(data),
    };
    return this.client(url, domain, token, requestOptions);
  }

  public async delete<T>(
    url: string,
    domain: Domains,
    token: string,
    data?: T,
    options?: RequestInit
  ): Promise<any> {
    const requestOptions = (data && {
      ...options,
      method: 'DELETE',
      body: JSON.stringify(data),
    }) || { ...options, method: 'DELETE' };
    return this.client(url, domain, token, requestOptions);
  }

  private addAuthHeader(
    initialHeaders: HeadersInit | undefined,
    token: string
  ): any {
    const headers = {
      ...initialHeaders,
      Authorization: `Bearer ${token}`,
    };
    return headers;
  }

  private decorateOptions(token: string, options?: RequestInit) {
    return {
      ...options,
      headers: {
        ...this.addAuthHeader(options ? options.headers : {}, token),
        'X-PANALYT-CSRF-PROTECTION':
          this.environmentService.getCSRFProtectionToken(),
        ...getAndCreatePanalytHeaders(),
      },
    };
  }
}
